import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, MapPin, Package, X } from "lucide-react";

const EMPTY_ADDR = {
  label: "Home",
  full_name: "",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postal_code: "",
  country: "",
  phone: "",
  is_default: false,
};

export default function AccountPage() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [editing, setEditing] = useState(null);

  const fetchAll = async () => {
    try {
      const [o, a] = await Promise.all([
        api.get("/account/orders"),
        api.get("/account/addresses"),
      ]);
      setOrders(o.data);
      setAddresses(a.data);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  useEffect(() => {
    if (user) fetchAll();
  }, [user]);

  if (loading) return <div className="pt-32 px-6">Loading…</div>;
  if (!user) return <Navigate to="/" replace />;

  const save = async () => {
    try {
      if (editing.id) {
        const { id, ...payload } = editing;
        await api.put(`/account/addresses/${id}`, payload);
        toast.success("Address updated");
      } else {
        await api.post("/account/addresses", editing);
        toast.success("Address added");
      }
      setEditing(null);
      fetchAll();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Remove this address?")) return;
    await api.delete(`/account/addresses/${id}`);
    fetchAll();
  };

  return (
    <div className="pt-24 md:pt-32 px-6 md:px-12 pb-24 min-h-screen" data-testid="account-page">
      <div className="max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div className="flex items-center gap-5">
            {user.picture && (
              <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
            )}
            <div>
              <div className="label-eyebrow mb-2">Member</div>
              <h1 className="font-display text-4xl md:text-6xl tracking-tighter">{user.name}.</h1>
              <p className="text-sm text-black/60 mt-2">{user.email}</p>
            </div>
          </div>
          <button onClick={logout} data-testid="account-logout-btn" className="border border-black px-5 py-2 text-sm hover:bg-black hover:text-white transition-colors">
            Sign out
          </button>
        </div>

        <div className="border-b border-black/10 flex gap-1 mb-10">
          <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} testId="tab-orders">
            <Package size={14} /> Order history
          </TabBtn>
          <TabBtn active={tab === "addresses"} onClick={() => setTab("addresses")} testId="tab-addresses">
            <MapPin size={14} /> Shipping addresses
          </TabBtn>
        </div>

        {tab === "orders" && (
          <div className="space-y-4">
            {orders.length === 0 && (
              <div className="py-20 text-center">
                <p className="text-black/50 mb-6">No orders yet.</p>
                <Link to="/shop" className="bg-black text-white px-6 py-3 text-sm inline-block">
                  Browse the shop
                </Link>
              </div>
            )}
            {orders.map((o) => (
              <div key={o.id} data-testid={`order-${o.id}`} className="border border-black/10 p-6">
                <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="label-eyebrow text-black/50">{new Date(o.created_at).toLocaleString()}</div>
                    <h3 className="font-display text-2xl mt-1">Order · {o.id.slice(0, 8).toUpperCase()}</h3>
                  </div>
                  <div className="text-right">
                    <div className="label-eyebrow text-black/50">Total</div>
                    <div className="font-display text-3xl">${o.amount.toFixed(2)}</div>
                  </div>
                </div>
                <div className="border-t border-black/10 pt-4 grid sm:grid-cols-2 gap-4">
                  {o.items?.map((it, i) => (
                    <div key={i} className="flex items-center gap-3">
                      {it.product && (
                        <img src={it.product.image_url} alt={it.product.name} className="w-14 h-14 object-cover bg-[#F7F5F0]" />
                      )}
                      <div className="text-sm">
                        <div className="font-display text-base">{it.product?.name || "Item"}</div>
                        <div className="text-black/50">Qty {it.quantity}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "addresses" && (
          <div>
            <button
              onClick={() => setEditing({ ...EMPTY_ADDR })}
              data-testid="new-address-btn"
              className="mb-6 inline-flex items-center gap-2 bg-black text-white px-5 py-3 text-sm hover:bg-[#C9A961] transition-colors"
            >
              <Plus size={14} /> Add address
            </button>

            {addresses.length === 0 && (
              <p className="text-black/50">No addresses saved yet.</p>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              {addresses.map((a) => (
                <div key={a.id} data-testid={`address-${a.id}`} className="border border-black/10 p-5 relative">
                  {a.is_default && (
                    <span className="absolute top-3 right-3 label-eyebrow text-[#C9A961]">Default</span>
                  )}
                  <div className="label-eyebrow text-black/50">{a.label}</div>
                  <div className="font-display text-lg mt-1">{a.full_name}</div>
                  <div className="text-sm text-black/70 mt-2 leading-relaxed">
                    {a.line1}
                    {a.line2 && <>, {a.line2}</>}<br />
                    {a.city}{a.state && `, ${a.state}`} {a.postal_code}<br />
                    {a.country}
                    {a.phone && <><br />{a.phone}</>}
                  </div>
                  <div className="mt-4 flex gap-3 text-sm">
                    <button onClick={() => setEditing(a)} className="inline-flex items-center gap-1 hover:text-[#C9A961]">
                      <Pencil size={13} /> Edit
                    </button>
                    <button onClick={() => del(a.id)} className="inline-flex items-center gap-1 hover:text-red-600">
                      <Trash2 size={13} /> Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="address-modal">
            <div className="p-6 flex items-center justify-between border-b border-black/10">
              <h2 className="font-display text-xl">{editing.id ? "Edit address" : "New address"}</h2>
              <button onClick={() => setEditing(null)}><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <Input label="Label" value={editing.label} onChange={(v) => setEditing({ ...editing, label: v })} testId="addr-label" />
              <Input label="Full name" value={editing.full_name} onChange={(v) => setEditing({ ...editing, full_name: v })} testId="addr-name" />
              <Input label="Address line 1" value={editing.line1} onChange={(v) => setEditing({ ...editing, line1: v })} testId="addr-line1" />
              <Input label="Address line 2 (optional)" value={editing.line2} onChange={(v) => setEditing({ ...editing, line2: v })} testId="addr-line2" />
              <div className="grid grid-cols-2 gap-4">
                <Input label="City" value={editing.city} onChange={(v) => setEditing({ ...editing, city: v })} testId="addr-city" />
                <Input label="State / Region" value={editing.state} onChange={(v) => setEditing({ ...editing, state: v })} testId="addr-state" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Postal code" value={editing.postal_code} onChange={(v) => setEditing({ ...editing, postal_code: v })} testId="addr-postal" />
                <Input label="Country" value={editing.country} onChange={(v) => setEditing({ ...editing, country: v })} testId="addr-country" />
              </div>
              <Input label="Phone (optional)" value={editing.phone} onChange={(v) => setEditing({ ...editing, phone: v })} testId="addr-phone" />
              <label className="flex items-center gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={editing.is_default}
                  onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })}
                  data-testid="addr-default"
                />
                Set as default address
              </label>
            </div>
            <div className="p-6 border-t border-black/10 flex gap-3 justify-end">
              <button onClick={() => setEditing(null)} className="px-5 py-2 border border-black/30 text-sm">Cancel</button>
              <button onClick={save} data-testid="addr-save" className="px-5 py-2 bg-black text-white text-sm hover:bg-[#C9A961] transition-colors">
                {editing.id ? "Save" : "Add"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`px-4 py-3 text-sm flex items-center gap-2 border-b-2 -mb-px transition-colors ${
        active ? "border-black" : "border-transparent text-black/50 hover:text-black"
      }`}
    >
      {children}
    </button>
  );
}

function Input({ label, value, onChange, testId }) {
  return (
    <label className="block">
      <span className="label-eyebrow block mb-2">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full border border-black/30 px-4 py-3 focus:border-black focus:outline-none"
      />
    </label>
  );
}
